import { useContext } from 'react';
import { AjaxContext, AppContext } from 'commons-ui';
import { API_ENDPOINTS, MESSAGES, REQUEST_TIMEOUT_MS } from '../constants';
import { DpdpaResponse, ExportAudit, ExportRequest, FetchRequest, SearchRequest, SearchResult, UserData } from '../types';

/**
 * Every call resolves to the response `data` on success, or `null` after alerting the error.
 * The backend reports failures as HTTP 200 `{ success: false, error, code }`.
 * `returnResponse` keeps postData from redirecting to /error on client timeouts / network errors,
 * which would throw away the agent's in-progress context.
 */
export default function useDpdpaService() {
	const { postData } = useContext(AjaxContext);
	const {
		app: { appName },
		showAlert
	} = useContext(AppContext);

	const call = async <T>(endpoint: string, body: object): Promise<T | null> => {
		try {
			const resp: any = await postData({
				endpoint,
				queryParams: { appName },
				body,
				timeout: REQUEST_TIMEOUT_MS,
				returnResponse: true
			});
			const respData: DpdpaResponse<T> | undefined = resp?.data;
			if (!respData) {
				showAlert(MESSAGES.NO_RESPONSE, { type: 'error' });
				return null;
			}
			// Not the JSON envelope: a plain-text 401/403 from the auth middleware, or an HTML gateway error page.
			// postData doesn't expose the status here, so don't claim which one it was.
			if (typeof respData !== 'object') {
				showAlert(MESSAGES.UNEXPECTED_RESPONSE, { type: 'error' });
				return null;
			}
			if (!respData.success || !respData.data) {
				showAlert(respData.error || MESSAGES.GENERIC_ERROR, { type: 'error' });
				return null;
			}
			return respData.data;
		} catch (err: any) {
			showAlert(err?.response?.data?.error || MESSAGES.GENERIC_ERROR, { type: 'error' });
			return null;
		}
	};

	const searchUser = (body: SearchRequest) => call<SearchResult>(API_ENDPOINTS.SEARCH_USER, body);

	// admin.service's authorization middleware forwards `userId` to auth-service for per-target-user checks.
	const fetchUserData = (body: FetchRequest) => call<UserData>(API_ENDPOINTS.FETCH_USER_DATA, { ...body, userId: body.uid });

	const auditExport = (body: ExportRequest) => call<ExportAudit>(API_ENDPOINTS.EXPORT_USER_DATA, { ...body, userId: body.uid });

	return { searchUser, fetchUserData, auditExport };
}
